import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import {
  Checkbox,
  getFontCss,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAllUsersFromState } from 'src/app/AccountTransactions/redux/accountTransactions.selectors'
import UtilityToolbarDownload from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDownload'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import FilterSection from 'src/components/FilterSection/FilterSection'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import NavAwayBox, { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import RadioButton from 'src/components/RadioButton/RadioButton'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
} from 'src/components/UserDropdown/UserDropdown'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import VirtualCardsTable from 'src/components/VirtualCardsTable/VirtualCardsTable'
import {
  VirtualCardDeactivatingInIncrements,
  VirtualCardFilterStatus,
  VirtualCardTypes,
} from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { useQueryOrganizationVirtualCardTypes } from 'src/queries/organizations/virtualCardTypes/virtualCardTypes.queries'
import { getUsers } from 'src/redux/actions/data/users.actions'
import { createGetVirtualCardsInitAction } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

export interface VirtualCardsBoxProps {
  defaultTitle?: React.ReactElement
  virtualCards: VirtualCardListModel[]
  virtualCardsPermissions?: Array<{
    permissions: VirtualCardPermissions[]
    virtualCardId: string
  }>
  emptyStateDesign: TableEmptyStateDesigns
  isYourCards?: boolean
  loading: boolean
  complete: boolean
  totalCount: number
  fullProps?: {
    filteredCount?: number
    keywordFilter: string
    setKeywordFilter: (value: string) => void
    statusesFilter: string[]
    setStatusesFilter: (value: Set<string>) => void
    handleStatusSelection: (value: string) => void
    recipientFilter: string[]
    setRecipientFilter: (value: Set<string>) => void
    handleRecipientSelection: (value: string) => void
    setPushNewSearchState?: (value: boolean) => void
    cardTypesFilter: string[]
    setCardTypesFilter: (value: Set<string>) => void
    handleCardTypesSelection: (value: string) => void
    deactivatingInFilter?: string
    setDeactivatingInFilter?: (value: string) => void
    sortableProps: {
      setSort: (field: string) => void
      sortBy?: string
      sortDescending?: boolean
    }
  }
  hideFilters?: boolean
  snapshotProps?: {
    navAway: NavAwayProps
  }
  isBudget?: boolean
  readOnly?: boolean
  isSnapshot?: boolean
  usersFullPaginatedData: UsersFullPaginatedDataState
  checkboxProps?: CheckboxProps
  infiniteScrollProps?: InfiniteScrollProps<VirtualCardsFiltersBody>
  filtersBody?: VirtualCardsFiltersBody
  onDownloadClick?: (filters: VirtualCardsFiltersBody) => void
  clientHeight?: number
  getVirtualCardDetailUrl?: (virtualCardId: string) => string
}

export interface CheckboxProps {
  selectedVirtualCards: VirtualCardListModel[]
  handleVirtualCardCheckboxClick: (option: VirtualCardListModel) => void
  handleBulkVirtualCardCheckboxClick: () => void
  handleDataGridVirtualCardCheckboxClick: (
    selection: GridRowSelectionModel,
  ) => void
  total: number
  canSelectVirtualCard: (virtualCard: VirtualCardListModel) => boolean
}

const VirtualCardsBox: React.FC<VirtualCardsBoxProps> = (
  props,
): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()

  const recipientOptions = useSelector(selectAllUsersFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { recurringVirtualCardsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )
  const user = useSelector(selectUserListModelFromState)

  const enableServerCardTypes =
    useSelector(selectFeatureFlaggerFromState)?.enable_server_card_types ??
    false

  const { data: virtualCardTypes } = useQueryOrganizationVirtualCardTypes({
    orgId: user?.organizationId,
    enabled: enableServerCardTypes,
  })

  // When server card types is disabled, fall back to pre-API behavior
  const showStandard = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.standard)
    : true
  const showRecurring = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.recurring) &&
      recurringVirtualCardsEnabled
    : recurringVirtualCardsEnabled
  const showGuestCard = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.guestAccess)
    : true
  const showGuestRecurring = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.recurringGuest) &&
      recurringVirtualCardsEnabled
    : recurringVirtualCardsEnabled
  const showDecliningBalance = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.decliningBalance)
    : false
  const showIssuerCycle = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.issuerCycle)
    : false

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers({
        q: filterInput,
        count: 10,
        page: page,
      }),
    )
  }
  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...props.usersFullPaginatedData,
  }

  const pushUrlStateDebounce = useCallback(
    _.debounce(() => {
      props.fullProps?.setPushNewSearchState?.(true)
    }, 500),
    [],
  )

  const onKeywordChange = (v: string): void => {
    props.fullProps?.setKeywordFilter?.(v)
  }

  const [resetAllFilters, setResetAllFilters] = useState<boolean>(false) // TODO: verify if this is needed

  const resetFilterValues = useMemo(
    () => ({
      keyword: '',
      status: new Set<string>(),
      recipient: new Set<string>(),
      cardTypes: new Set<string>(),
      deactivatingIn: '',
    }),
    [],
  )

  const resetKeywordFilter = (): void => {
    props.fullProps?.setKeywordFilter(resetFilterValues.keyword)
  }
  const resetStatusSet = (): void => {
    props.fullProps?.setStatusesFilter(resetFilterValues.status)
  }
  const resetRecipient = (): void => {
    props.fullProps?.setRecipientFilter(resetFilterValues.recipient)
  }
  const resetCardTypesSet = (): void => {
    props.fullProps?.setCardTypesFilter(resetFilterValues.cardTypes)
  }
  const resetDeactivatingIn = (): void => {
    props.fullProps?.setDeactivatingInFilter?.(resetFilterValues.deactivatingIn)
  }

  const onFiltersResetClick = (): void => {
    setResetAllFilters(true)
    resetKeywordFilter()
    resetStatusSet()
    resetRecipient()
    resetCardTypesSet()
    resetDeactivatingIn()
    pushUrlStateDebounce()
  }

  const isDirty =
    props.fullProps?.keywordFilter ||
    (props.fullProps?.statusesFilter &&
      props.fullProps?.statusesFilter.length > 0) ||
    (props.fullProps?.recipientFilter &&
      props.fullProps?.recipientFilter.length > 0) ||
    (props.fullProps?.cardTypesFilter &&
      props.fullProps?.cardTypesFilter.length > 0) ||
    props.fullProps?.deactivatingInFilter

  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (
      props.fullProps?.statusesFilter &&
      props.fullProps?.statusesFilter.length > 0
    ) {
      appliedCount++
    }
    if (
      props.fullProps?.recipientFilter &&
      props.fullProps?.recipientFilter.length > 0
    ) {
      appliedCount++
    }
    if (
      props.fullProps?.cardTypesFilter &&
      props.fullProps?.cardTypesFilter.length > 0
    ) {
      appliedCount++
    }
    if (props.fullProps?.deactivatingInFilter) {
      appliedCount++
    }
    return appliedCount
  }

  const handleSelection = (): void => {
    // call init action to set loading state immediately
    dispatch(createGetVirtualCardsInitAction())
    pushUrlStateDebounce()
  }

  const isEmpty =
    !props.loading &&
    (props.virtualCards.length === 0 || props.fullProps?.filteredCount === 0)

  return (
    <NavAwayBox
      navAway={
        props.totalCount > 0 && props.snapshotProps !== undefined
          ? props.snapshotProps.navAway
          : undefined
      }
    >
      <FilterSection
        hideButtonChevron
        appliedFiltersCount={filtersAppliedCount()}
        additionalFilterElement={
          props.onDownloadClick !== undefined ? (
            <UtilityToolbarDownload
              onClick={(): void =>
                props.onDownloadClick?.(props.filtersBody || {})
              }
            />
          ) : undefined
        }
        filtersFlyoutProps={
          props.fullProps !== undefined && !props.hideFilters
            ? {
                title: translate('Virtual Card Filters'),
                onReset: onFiltersResetClick,
                loading: props.loading,
                filters: (
                  <>
                    <div
                      role="group"
                      aria-labelledby="filter-card-status"
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                      `}
                    >
                      <div
                        id="filter-card-status"
                        css={css`
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        <Trns source={'Card Status'} />
                      </div>
                      <Checkbox
                        label={<Trns source={'Active'} />}
                        testId="activeChk"
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.active,
                          )
                          handleSelection()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.active,
                          ) || false
                        }
                      />
                      <Checkbox
                        label={<Trns source={'Pending'} />}
                        testId="pendingChk"
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.pending,
                          )
                          handleSelection()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.pending,
                          ) || false
                        }
                      />
                      <Checkbox
                        label={<Trns source={'Inactive'} />}
                        testId="inactiveChk"
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.inactive,
                          )
                          handleSelection()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.inactive,
                          ) || false
                        }
                      />
                      <Checkbox
                        label={<Trns source={'Closed'} />}
                        testId="closedChk"
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.closed,
                          )
                          handleSelection()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.closed,
                          ) || false
                        }
                      />
                    </div>
                    <DividerLineDiv
                      css={css`
                        margin: 20px 0px;
                      `}
                    />
                    {(showStandard ||
                      showRecurring ||
                      showGuestCard ||
                      showGuestRecurring ||
                      showDecliningBalance ||
                      showIssuerCycle) && (
                      <>
                        <div
                          role="group"
                          aria-labelledby="filter-card-type"
                          css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                          `}
                        >
                          <div
                            id="filter-card-type"
                            css={css`
                              ${getFontCss(
                                14,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.medium,
                              )};
                            `}
                          >
                            <Trns source={'Card Type'} />
                          </div>
                          {showStandard && (
                            <Checkbox
                              label={<Trns source={'Standard'} />}
                              testId="standardChk"
                              onClick={(): void => {
                                props.fullProps?.handleCardTypesSelection(
                                  VirtualCardTypes.standard,
                                )
                                handleSelection()
                              }}
                              isSelected={
                                props.fullProps?.cardTypesFilter.includes(
                                  VirtualCardTypes.standard,
                                ) || false
                              }
                            />
                          )}
                          {showRecurring && (
                            <Checkbox
                              label={<Trns source={'Recurring'} />}
                              testId="recurringChk"
                              onClick={(): void => {
                                props.fullProps?.handleCardTypesSelection(
                                  VirtualCardTypes.recurring,
                                )
                                handleSelection()
                              }}
                              isSelected={
                                props.fullProps?.cardTypesFilter.includes(
                                  VirtualCardTypes.recurring,
                                ) || false
                              }
                            />
                          )}
                          {showGuestCard && (
                            <Checkbox
                              label={<Trns source={'Guest Card'} />}
                              testId="guestChk"
                              onClick={(): void => {
                                props.fullProps?.handleCardTypesSelection(
                                  VirtualCardTypes.guestAccess,
                                )
                                handleSelection()
                              }}
                              isSelected={
                                props.fullProps?.cardTypesFilter.includes(
                                  VirtualCardTypes.guestAccess,
                                ) || false
                              }
                            />
                          )}
                          {showGuestRecurring && (
                            <Checkbox
                              label={<Trns source={'Guest Card Recurring'} />}
                              testId="guestRecurringChk"
                              onClick={(): void => {
                                props.fullProps?.handleCardTypesSelection(
                                  VirtualCardTypes.recurringGuest,
                                )
                                handleSelection()
                              }}
                              isSelected={
                                props.fullProps?.cardTypesFilter.includes(
                                  VirtualCardTypes.recurringGuest,
                                ) || false
                              }
                            />
                          )}
                          {showDecliningBalance && (
                            <Checkbox
                              label={<Trns source={'Declining Balance'} />}
                              testId="decliningBalanceChk"
                              onClick={(): void => {
                                props.fullProps?.handleCardTypesSelection(
                                  VirtualCardTypes.decliningBalance,
                                )
                                handleSelection()
                              }}
                              isSelected={
                                props.fullProps?.cardTypesFilter.includes(
                                  VirtualCardTypes.decliningBalance,
                                ) || false
                              }
                            />
                          )}
                          {showIssuerCycle && (
                            <Checkbox
                              label={translate('Recurring')}
                              testId="issuerCycleChk"
                              onClick={(): void => {
                                props.fullProps?.handleCardTypesSelection(
                                  VirtualCardTypes.issuerCycle,
                                )
                                handleSelection()
                              }}
                              isSelected={
                                props.fullProps?.cardTypesFilter.includes(
                                  VirtualCardTypes.issuerCycle,
                                ) || false
                              }
                            />
                          )}
                        </div>
                        <DividerLineDiv
                          css={css`
                            margin: 20px 0px;
                          `}
                        />
                      </>
                    )}
                    <div
                      role="radiogroup"
                      aria-labelledby="filter-deactivating-within"
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                      `}
                    >
                      <div
                        id="filter-deactivating-within"
                        css={css`
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        <Trns source={'Cards Deactivating Within'} />
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            props.fullProps?.setDeactivatingInFilter?.(
                              VirtualCardDeactivatingInIncrements.in7Days,
                            )
                            handleSelection()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter ===
                            VirtualCardDeactivatingInIncrements.in7Days
                          }
                          css={css`
                            display: block;
                          `}
                          testId="7daysRadio"
                          aria-label={translate('7 days')}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          {translate('7 days')}
                        </div>
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            props.fullProps?.setDeactivatingInFilter?.(
                              VirtualCardDeactivatingInIncrements.in14Days,
                            )
                            handleSelection()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter ===
                            VirtualCardDeactivatingInIncrements.in14Days
                          }
                          css={css`
                            display: block;
                          `}
                          testId="14daysRadio"
                          aria-label={translate('14 days')}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          {translate('14 days')}
                        </div>
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            props.fullProps?.setDeactivatingInFilter?.(
                              VirtualCardDeactivatingInIncrements.in30Days,
                            )
                            handleSelection()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter ===
                            VirtualCardDeactivatingInIncrements.in30Days
                          }
                          css={css`
                            display: block;
                          `}
                          testId="30daysRadio"
                          aria-label={translate('30 days')}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          {translate('30 days')}
                        </div>
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            resetDeactivatingIn()
                            handleSelection()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter === ''
                          }
                          css={css`
                            display: block;
                          `}
                          testId="anytimeRadio"
                          aria-label={translate('Any time')}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          {translate('Any time')}
                        </div>
                      </div>
                    </div>
                    {!props.isYourCards && (
                      <>
                        <DividerLineDiv
                          css={css`
                            margin: 20px 0px;
                          `}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.medium,
                            )};
                          `}
                        >
                          <Trns source={'Card User'} />
                        </div>
                        <UserDropdown
                          css={css`
                            margin-top: 8px;
                          `}
                          data-testid="cardUserDropdown"
                          isCreatable={false}
                          placeholder={translateSingleString(
                            locale,
                            'All virtual card users',
                          )}
                          chooseOption={(val: string): void => {
                            props.fullProps?.handleRecipientSelection(val)
                            handleSelection()
                          }}
                          isMulti
                          selectedValues={
                            new Set<string>(props.fullProps?.recipientFilter)
                          }
                          optionGroups={[
                            {
                              options: recipientOptions,
                            },
                          ]}
                          externalReset={{
                            resetClicked: resetAllFilters,
                            setResetClicked: setResetAllFilters,
                          }}
                          onResetClick={(): void => {
                            resetRecipient()
                            pushUrlStateDebounce()
                          }}
                          infiniteScrollProps={userInfiniteScrollProps}
                          minimalDisplay
                        />
                      </>
                    )}
                  </>
                ),
              }
            : undefined
        }
        topLeftContent={
          <div
            css={css`
              display: flex;
              width: 100%;
              align-items: center;
              justify-content: space-between;
            `}
          >
            {isDirty &&
              props.fullProps &&
              (props.loading && !props.complete ? (
                <Processing />
              ) : (
                <span
                  css={css`
                    ${getFontCss(
                      20,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {props.totalCount === 1 ? (
                    props.fullProps.filteredCount &&
                    props.fullProps.filteredCount === 1 ? (
                      <Trns
                        source={
                          '${filteredCount} Result (of ${totalCount} card)'
                        }
                        expressions={{
                          filteredCount: `${props.fullProps.filteredCount || 0}`,
                          totalCount: `${props.totalCount}`,
                        }}
                      />
                    ) : (
                      <Trns
                        source={
                          '${filteredCount} Results (of ${totalCount} card)'
                        }
                        expressions={{
                          filteredCount: `${props.fullProps.filteredCount || 0}`,
                          totalCount: `${props.totalCount}`,
                        }}
                      />
                    )
                  ) : props.fullProps.filteredCount &&
                    props.fullProps.filteredCount === 1 ? (
                    <Trns
                      source={
                        '${filteredCount} Result (of ${totalCount} cards)'
                      }
                      expressions={{
                        filteredCount: `${props.fullProps.filteredCount || 0}`,
                        totalCount: `${props.totalCount}`,
                      }}
                    />
                  ) : (
                    <Trns
                      source={
                        '${filteredCount} Results (of ${totalCount} cards)'
                      }
                      expressions={{
                        filteredCount: `${props.fullProps.filteredCount || 0}`,
                        totalCount: `${props.totalCount}`,
                      }}
                    />
                  )}
                </span>
              ))}
            {!isDirty && props.defaultTitle !== undefined && props.defaultTitle}
            {props.fullProps && (
              <div
                css={css`
                  flex-grow: 1;
                  padding-left: ${(isDirty && props.fullProps) ||
                  props.defaultTitle !== undefined
                    ? 40
                    : 0}px;
                `}
              >
                <KeywordFilter
                  value={props.fullProps.keywordFilter}
                  onChange={(val: string): void => {
                    // call init action to set loading state immediately
                    dispatch(createGetVirtualCardsInitAction())
                    onKeywordChange(val)
                    pushUrlStateDebounce()
                  }}
                  placeholder={translateSingleString(
                    locale,
                    'Name, Available Credit, Last 4, etc.',
                  )}
                />
              </div>
            )}
          </div>
        }
      />
      <div
        css={css`
          ${!props.isSnapshot &&
          css`
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
          `}
        `}
      >
        {!isEmpty && (
          <VirtualCardsTable
            virtualCards={props.virtualCards}
            virtualCardsPermissions={props.virtualCardsPermissions}
            sortableProps={props.fullProps?.sortableProps}
            isYourCards={!!props.isYourCards}
            checkboxProps={props.checkboxProps}
            infiniteScrollProps={props.infiniteScrollProps}
            filtersBody={props.filtersBody}
            clientHeight={props.clientHeight}
            getVirtualCardDetailUrl={props.getVirtualCardDetailUrl}
          />
        )}
        {props.complete &&
          (isDirty ? (
            <>
              {props.fullProps?.filteredCount === 0 && (
                <TableEmptyState
                  css={css`
                    background-color: ${styleConstants.colors.gray_lightest};
                    border-bottom-left-radius: 10px;
                    border-bottom-right-radius: 10px;
                  `}
                  design={TableEmptyStateDesigns.VirtualCardsFiltered}
                  readOnly={!!props.readOnly}
                />
              )}
            </>
          ) : (
            <>
              {props.totalCount === 0 && (
                <TableEmptyState
                  css={css`
                    background-color: ${styleConstants.colors.gray_lightest};
                    border-bottom-left-radius: 10px;
                    border-bottom-right-radius: 10px;
                  `}
                  design={props.emptyStateDesign}
                  readOnly={!!props.readOnly}
                />
              )}
            </>
          ))}
      </div>
    </NavAwayBox>
  )
}

export default VirtualCardsBox
